import React, {ReactElement, useEffect, useRef, useState} from "react";
import {OptionProps} from "./option/uiOption";
import "./uiselect.css"
import {useTranslation} from "react-i18next";

type SelectProps = {
    children: ReactElement<OptionProps> | ReactElement<OptionProps>[],
    onChange?: (value: any) => void,
    search?: boolean
    searchPlaceholder?: string
    selectOptionPlaceholder?: string
    defaultValue?: any,
    className?: string,
    onOpenClearSearch?: boolean,
    onError?: boolean,
}

export const UiSelect: React.FC<SelectProps> = (
    {
        children,
        onChange,
        search = true,
        searchPlaceholder,
        selectOptionPlaceholder,
        defaultValue,
        className = "",
        onOpenClearSearch = false,
        onError= false,
    }) => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState("");
    const [showOptions, setShowOptions] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);
    const [lastSelectedValue, setLastSelectedValue] = useState("");
    const lastSelectedValueRef = useRef(lastSelectedValue);

    const getNewSearchValue = (optionProps: OptionProps) => {
        if (Array.isArray(optionProps.children)) {
            for (const child of optionProps.children) {
                if (typeof child === "string") {
                    return child;
                }
            }
        }

        return (optionProps.searchData ?? optionProps.children).toString();
    }

    useEffect(() => {
        lastSelectedValueRef.current = lastSelectedValue;
    }, [lastSelectedValue]);

    const handleOptionClick = (optionProps: OptionProps) => {
        const newValue = getNewSearchValue(optionProps);
        setSearchValue(newValue);
        
        if (onOpenClearSearch) {
            setLastSelectedValue(newValue);
        }
        
        setShowOptions(false);
        if (onChange) onChange(optionProps.value);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
        setShowOptions(true);
    };

    const handleDivClick = () => {
        if (onOpenClearSearch) {
            setSearchValue("");
        }
        setShowOptions(prevState => !prevState);
    };

    const setDefaultValue = () => {
        if (!children || React.Children.count(children) === 0 || !defaultValue || defaultValue == lastSelectedValue) {
            return;
        }

        if (!Array.isArray(children)) {
            if (children.props.value !== defaultValue) {
                return
            }

            handleOptionClick(children.props);
        } else {
            for (const child of children) {
                if (child.props.value === defaultValue) {
                    handleOptionClick(child.props);

                    if (onOpenClearSearch) {
                        const newValue = getNewSearchValue(child.props);
                        setLastSelectedValue(newValue);
                    }
                    break;
                }
            }
        }
    }

    useEffect(function setListenerAndDefaultValue() {
        setDefaultValue();

        const handleClickOutside = (event: any) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setShowOptions(false);
                
                if (onOpenClearSearch) {
                    setSearchValue(lastSelectedValueRef.current);
                }
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(function updateDefaultValue() {
        setDefaultValue()
    }, [defaultValue]);

    const filteredChildren = React.Children.toArray(children).filter((child) =>
        React.isValidElement(child) &&
        (child.props.searchData ?? child.props.value)
            .toString().toLowerCase().includes(searchValue.toLowerCase())
    );

    return (
        <div
            className={"rounded-xl h-12" + (className ? ` ${className}` : "")}
            ref={selectRef}
        >
            <div
                onClick={handleDivClick}
                className={"bg-deep-blue flex items-center h-full" +
                    (showOptions ? " rounded-t-xl border-x-2 border-t-2" : " rounded-xl border-2") +
                    (onError ? " border-danger" : " border-darkpurple-blue")
                }
            >
                <div className="flex w-full lg:w-fit">
                    <input
                        type="text"
                        placeholder={search
                            ? (searchPlaceholder ?? t("global.search"))
                            : (selectOptionPlaceholder ?? t("form.select-option"))
                        }
                        className={
                            "bg-deep-blue rounded-xl pl-2.5 py-1 outline-0 w-full"
                            + (!search ? " cursor-default": "")
                        }
                        value={searchValue}
                        onChange={handleSearchChange}
                        readOnly={!search}
                    />

                    <img
                        className={showOptions ? "pr-2" : "pl-2 rotate-180"}
                        src="/icons/arrow.svg"
                        alt={t("global.expand-arrow")}
                    />
                </div>
            </div>

            {showOptions && <div
                className={"overflow-y-auto overflow-x-hidden z-50 relative pb-2 " + 
                    "bg-deep-blue border-darkpurple-blue max-h-28 border-x-2 border-b-2 rounded-b-xl" +
                    (onError ? " border-danger" : " border-darkpurple-blue")
                }
            >
                <hr className="border-0 bg-darkpurple-blue h-0.5 w-10/12 mb-1 mx-auto" />

                {search && filteredChildren.map((child) => {
                    if (React.isValidElement<OptionProps>(child)) {
                        return React.cloneElement(child, {
                            _onClick: () => handleOptionClick(child.props)
                        });
                    }

                    return null;
                })}

                {search && filteredChildren.length === 0 && <p className="m-1 text-center">
                    {t("global.nothing-found")}
                </p>}

                {!search && (Array.isArray(children) ? children.map((child) => {
                    if (React.isValidElement<OptionProps>(child)) {
                        return React.cloneElement(child, {
                            _onClick: () => handleOptionClick(child.props)
                        });
                    }

                    return null;
                }) : (React.isValidElement<OptionProps>(children) ? React.cloneElement(children, {
                    _onClick: () => handleOptionClick(children.props)
                }) : null))}
            </div>}
        </div>
    );
}
